import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { ApplicationContract, EnvironmentMode } from '../../contracts/application';
import { 
    selectApplications, 
    selectScopes, 
    selectLoading, 
    selectError,
    selectCredentialsBeingCreated 
} from '../../store/Applications/selectors';
import { setEnvironment as setApplicationsEnvironment, setError as clearApplicationError, setCredentialsBeingCreated } from '../../store/Applications/reducer';
import { 
    fetchApplicationsThunk, 
    createApplicationThunk, 
    deleteApplicationThunk, 
    renameApplicationThunk, 
    createSecretThunk, 
    deleteSecretThunk, 
    toggleScopeThunk 
} from '../../store/Applications/thunks';
import config from '../../config/appConfig';
import ApplicationCard from './ApplicationCard';
import CreateApplicationForm from './CreateApplicationForm';

interface ApplicationManagerProps {
    environment: 'sandbox' | 'production';
    userId: string;
}

const ApplicationManager: React.FC<ApplicationManagerProps> = ({ environment, userId }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    
    // Redux state
    const applications = useSelector(selectApplications);
    const scopes = useSelector(selectScopes);
    const loading = useSelector(selectLoading);
    const error = useSelector(selectError);
    const credentialsBeingCreated = useSelector(selectCredentialsBeingCreated);

    // Use singleton applicationService from config
    const applicationLimit = config.features.applicationLimit;
    const secretLimit = config.features.secretLimit;

    useEffect(() => {
        dispatch(setApplicationsEnvironment(environment));
        dispatch(fetchApplicationsThunk(userId) as any);
    }, [dispatch, environment, userId]);

    // Clear Redux error silently (no snackbar/alert)
    useEffect(() => {
        if (error) {
            dispatch(clearApplicationError(null));
        }
    }, [error, dispatch]);

    const handleCreateApplication = async (name: string, scopeIds: string[]) => {
        try {
            await dispatch(createApplicationThunk(userId, name, scopeIds) as any);
        } catch (err: any) {
            console.error('Failed to create application:', err);
        }
    };

    const handleDeleteApplication = async (applicationId: string) => {
        try {
            await dispatch(deleteApplicationThunk(userId, applicationId) as any);
        } catch (err: any) {
            console.error('Failed to delete application:', err);
        }
    };

    const handleRenameApplication = async (applicationId: string, newName: string) => {
        try {
            await dispatch(renameApplicationThunk(userId, applicationId, newName) as any);
        } catch (err: any) {
            console.error('Failed to rename application:', err);
        }
    };

    const handleCreateSecret = async (
        applicationId: string,
        secretName: string,
        expirationDate: string
    ) => {
        try {
            await dispatch(createSecretThunk(userId, applicationId, secretName, expirationDate) as any);
        } catch (err: any) {
            console.error('Failed to create secret:', err);
        }
    };

    const handleDeleteSecret = async (applicationId: string, secretId: string) => {
        try {
            await dispatch(deleteSecretThunk(userId, applicationId, secretId) as any);
        } catch (err: any) {
            console.error('Failed to delete secret:', err);
        }
    };

    const handleToggleScope = async (
        applicationId: string,
        scopeId: string,
        isAdding: boolean
    ) => {
        try {
            await dispatch(toggleScopeThunk(userId, applicationId, scopeId, isAdding) as any);
        } catch (err: any) {
            console.error('Failed to update scope:', err);
        }
    };

    const handleRetry = () => {
        dispatch(fetchApplicationsThunk(userId) as any);
    };

    if (loading && applications.length === 0) {
        return (
            <div className="merchant-manager">
                <div className="merchant-manager-header">
                    <h1 className="merchant-manager-title">
                        {t('nav.clientCredentials')} (OAuth2)
                    </h1>
                </div>
                <div className="loading-container">
                    <p className="loading-text">{t('common.loading')}</p>
                </div>
            </div>
        );
    }

    if (error && applications.length === 0) {
        return (
            <div className="merchant-manager">
                <div className="merchant-manager-header">
                    <h1 className="merchant-manager-title">
                        {t('nav.clientCredentials')} (OAuth2)
                    </h1>
                </div>
                <div className="error-container">
                    <p className="error-text">{error}</p>
                    <button onClick={handleRetry} className="retry-button">
                        {t('common.retry')}
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="application-manager">
            <div className="application-manager-header">
                <h1 className="application-manager-title">
                    {t('nav.clientCredentials')} (OAuth2)
                </h1>
                <hr className="section-title-divider" />
                <h2 className="application-title">{t('applications.createNew')}</h2>
            </div>

            <CreateApplicationForm
                    scopes={scopes}
                    environment={environment}
                    onCreate={handleCreateApplication}
                    applicationLimit={applicationLimit}
                    currentCount={applications.length}
                />

            <div className="application-list-section">
                <h2 className="application-list-title">{t('applications.manageClients')}</h2>
                {applications.length > 0 && (
                    <div className="application-list">
                        {applications.map((app) => (
                            <ApplicationCard
                                key={app.id}
                                application={app}
                                allScopes={scopes}
                                onDelete={handleDeleteApplication}
                                onRename={handleRenameApplication}
                                onCreateSecret={handleCreateSecret}
                                onDeleteSecret={handleDeleteSecret}
                                onToggleScope={handleToggleScope}
                                secretLimit={secretLimit}
                            />
                        ))}
                        {credentialsBeingCreated && (
                            <div className="application-card credentials-creating-card">
                                <div className="application-card-header" style={{ cursor: 'default' }}>
                                    <div className="application-header-left">
                                        <h3 className="application-name-message">{t('applications.credentialsBeingCreated', { defaultValue: 'Client Credentials are being created' })}</h3>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default ApplicationManager;

